import { FC } from "react";
import { IconButton, Menu, MenuItem } from "@mui/material";
import { FolderItem } from "global/components/Folders/types";
import { SignRequest } from "models/interfaces";
import { usePopover } from "hooks/use-popover";
import strings from "global/constants/StringConstants";
import signRequestsTokens from "../SignRequests.tokens";
import { KebabGlyph } from "./SignRequestsIcons";

const { kebab } = signRequestsTokens;

interface SignRequestItemMenuProps {
  item: FolderItem;
  isLoading?: boolean;
  /** Folder action labels (rename / delete / move / new subfolder). */
  folderMenuItems: string[];
  /** Runs a chosen folder action — opens the shared rename / move / delete dialog. */
  onFolderMenuItem: (item: FolderItem, label: string) => void;
  /** Opens the shared request actions menu, anchored to the clicked button. */
  onRequestMenu: (request: SignRequest, anchor: HTMLElement) => void;
}

/**
 * The per-item "more options" (⋮) menu for a Sign Requests search-result row.
 * It exposes the same actions the list rows do, by reusing their handlers:
 *  - a folder result opens its own rename / delete / move / new-subfolder menu
 *    and drives the shared folder dialog through `onFolderMenuItem`;
 *  - a request result opens the shared `RequestActionsMenu` (void, download,
 *    move, edit draft, …) through `onRequestMenu`, so the search menu can never
 *    drift from the row menu and the menu's confirmation dialogs live outside
 *    this dropdown's subtree.
 */
export const SignRequestItemMenu: FC<SignRequestItemMenuProps> = ({
  item,
  isLoading = false,
  folderMenuItems,
  onFolderMenuItem,
  onRequestMenu,
}) => {
  const popover = usePopover<HTMLButtonElement>();
  const isFolder = item.type === strings.folder;

  // Requests defer to the parent's shared RequestActionsMenu (rendered once in
  // the list) — same items, and its confirm dialogs sit above this dropdown.
  if (!isFolder) {
    return (
      <IconButton
        size="small"
        disabled={isLoading}
        aria-label={`Request options for ${item.emailSubject ?? "request"}`}
        aria-haspopup="true"
        onClick={(event) => {
          event.stopPropagation();
          onRequestMenu(item as unknown as SignRequest, event.currentTarget);
        }}
      >
        <KebabGlyph sx={{ width: kebab.iconSize, height: kebab.iconSize }} />
      </IconButton>
    );
  }

  if (folderMenuItems.length === 0) return null;

  return (
    <>
      <IconButton
        ref={popover.anchorRef}
        size="small"
        disabled={isLoading}
        aria-label={`Folder options for ${item.templateName ?? "folder"}`}
        aria-haspopup="true"
        aria-expanded={popover.open}
        onClick={(event) => {
          event.stopPropagation();
          popover.handleOpen();
        }}
      >
        <KebabGlyph sx={{ width: kebab.iconSize, height: kebab.iconSize }} />
      </IconButton>
      <Menu
        anchorEl={popover.anchorRef.current}
        anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
        transformOrigin={{ horizontal: "right", vertical: "top" }}
        open={popover.open}
        onClose={popover.handleClose}
        onClick={(event) => event.stopPropagation()}
      >
        {folderMenuItems.map((label) => (
          <MenuItem
            key={label}
            onClick={() => {
              popover.handleClose();
              onFolderMenuItem(item, label);
            }}
          >
            {label}
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};

export default SignRequestItemMenu;
